/**
 * BookingDetailsStep Component
 *
 * Contact form AND the commit step. The flow used to push the booker to a
 * separate `confirmation` screen that only re-displayed the summary sidebar, so
 * the review now lives inline here and the primary button books the meeting.
 * Every time renders in the booker's own zone (`bookingData.timeZone`).
 */

import React, { useCallback } from 'react';
import { User, Video, MapPin, Calendar, CheckCircle } from 'lucide-react';
import { parseISO } from 'date-fns';
import { Label } from '../../label';
import { Input } from '../../input';
import { Textarea } from '../../textarea';
import { Button } from '../../button';
import { BookingData } from '../utils/bookingConstants';
import { formatPersonName, formatSlotDate, formatTimeRange } from '../utils/bookingFormatters';
import { formatTimeZoneAbbreviation, formatTimeZoneDisplay } from '../utils/bookingTimezones';

interface BookingDetailsStepProps {
  bookingData: BookingData;
  fieldErrors: Record<string, string>;
  submissionError: any;
  eventTypeDescription: string | null;
  onBookingDataChange: (updates: Partial<BookingData>) => void;
  onFieldErrorClear: (field: string) => void;
}

export function BookingDetailsStep({
  bookingData,
  fieldErrors,
  submissionError,
  eventTypeDescription,
  onBookingDataChange,
  onFieldErrorClear,
}: BookingDetailsStepProps) {
  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onBookingDataChange({ name: e.target.value });
      if (fieldErrors.name) {
        onFieldErrorClear('name');
      }
    },
    [onBookingDataChange, fieldErrors.name, onFieldErrorClear]
  );

  const handleEmailChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onBookingDataChange({ email: e.target.value });
      if (fieldErrors.email) {
        onFieldErrorClear('email');
      }
    },
    [onBookingDataChange, fieldErrors.email, onFieldErrorClear]
  );

  const handleLocationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onBookingDataChange({ location: e.target.value });
      if (fieldErrors.location) {
        onFieldErrorClear('location');
      }
    },
    [onBookingDataChange, fieldErrors.location, onFieldErrorClear]
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onBookingDataChange({ description: e.target.value });
    },
    [onBookingDataChange]
  );

  const handleMeetingTypeChange = useCallback(
    (isOnline: boolean) => {
      onBookingDataChange({ isOnline });
    },
    [onBookingDataChange]
  );

  const personName = formatPersonName(bookingData.bookeeEmail);
  const timeZone = bookingData.timeZone;
  const slot = bookingData.selectedSlot;
  const slotStart = slot ? parseISO(slot.start) : undefined;
  const zoneAbbreviation = formatTimeZoneAbbreviation(timeZone, slotStart);

  return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <User className="w-16 h-16 mx-auto mb-4 text-blue-600" />
        <h3 className="text-lg font-semibold text-card-foreground mb-2">Your Meeting Details</h3>
        <p className="text-gray-600 mb-4">Please provide your contact information</p>

        {/* Error Summary */}
        {(Object.keys(fieldErrors).length > 0 || submissionError) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start space-x-2 text-red-800">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="flex-1">
                <p className="font-medium mb-1">Please fix the following errors:</p>
                <ul className="list-disc list-inside text-sm space-y-1">
                  {submissionError?.message && !submissionError?.fieldErrors && (
                    <li>{submissionError.message}</li>
                  )}
                  {Object.entries(fieldErrors).map(([field, error]) => (
                    <li key={field}>
                      {field === 'general'
                        ? error
                        : `${field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}: ${error}`}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Call-to-action notice — names the ACTUAL primary button. */}
        {Object.keys(fieldErrors).length === 0 && !submissionError && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-center space-x-2 text-green-800">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">
                Last step — &ldquo;Confirm booking&rdquo; sends the calendar invite
              </span>
            </div>
            <p className="text-sm text-green-700 mt-2">
              {bookingData.bookeeEmail
                ? `A calendar invitation goes to both you and ${personName}.`
                : 'A calendar invitation goes to both you and the WTC team member you are matched with.'}
            </p>
          </div>
        )}
      </div>

      {/* Selected Slot Display */}
      {bookingData.selectedSlot && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-full">
              {bookingData.selectedSlot.isLunchSlot ? (
                <User className="h-5 w-5 text-orange-600" />
              ) : (
                <Calendar className="h-5 w-5 text-blue-600" />
              )}
            </div>
            <div>
              <h4 className="font-medium text-card-foreground">
                {formatSlotDate(bookingData.selectedSlot.start, timeZone)}
              </h4>
              <p className="text-sm text-gray-600">
                {formatTimeRange(
                  bookingData.selectedSlot.start,
                  bookingData.selectedSlot.end,
                  timeZone
                )}{' '}
                <span className="font-medium text-gray-700">{zoneAbbreviation}</span>
                {bookingData.selectedSlot.isLunchSlot && (
                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    Lunch Meeting
                  </span>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <Label htmlFor="name" className="text-sm font-medium text-gray-700">
            Full Name *
          </Label>
          <Input
            id="name"
            type="text"
            placeholder="Enter your full name"
            value={bookingData.name}
            onChange={handleNameChange}
            className={`w-full ${fieldErrors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          />
          {fieldErrors.name && <p className="text-sm text-red-600 mt-1">{fieldErrors.name}</p>}
        </div>

        <div className="space-y-3">
          <Label htmlFor="email" className="text-sm font-medium text-gray-700">
            Email Address *
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="Enter your email"
            value={bookingData.email}
            onChange={handleEmailChange}
            className={`w-full ${fieldErrors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          />
          {fieldErrors.email && <p className="text-sm text-red-600 mt-1">{fieldErrors.email}</p>}
        </div>
      </div>

      {/* Meeting Type Selection */}
      <div className="space-y-3">
        <Label className="text-sm font-medium text-gray-700 block">Meeting Type</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={bookingData.isOnline ? 'default' : 'outline'}
            onClick={() => handleMeetingTypeChange(true)}
            className="flex-1 flex items-center gap-2"
          >
            <Video className="h-4 w-4" />
            Online Meeting
          </Button>
          <Button
            type="button"
            variant={!bookingData.isOnline ? 'default' : 'outline'}
            onClick={() => handleMeetingTypeChange(false)}
            className="flex-1 flex items-center gap-2"
          >
            <MapPin className="h-4 w-4" />
            In-Person
          </Button>
        </div>

        {/* Online Meeting Notice */}
        {bookingData.isOnline && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <Video className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Google Meet Link</p>
                <p>A Google Meet link will be automatically generated and included in the calendar invite.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Location Input - only show for in-person meetings */}
      {!bookingData.isOnline && (
        <div className="space-y-3">
          <Label htmlFor="location" className="text-sm font-medium text-gray-700">
            Meeting Location *
          </Label>
          <Input
            id="location"
            type="text"
            placeholder="e.g., Conference Room A, Office Address, etc."
            value={bookingData.location}
            onChange={handleLocationChange}
            className={`w-full ${fieldErrors.location ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''}`}
          />
          {fieldErrors.location && <p className="text-sm text-red-600 mt-1">{fieldErrors.location}</p>}
        </div>
      )}

      <div className="space-y-3">
        <Label htmlFor="description" className="text-sm font-medium text-gray-700">
          Meeting Description
        </Label>
        <Textarea
          id="description"
          placeholder="Brief description of what you'd like to discuss..."
          value={bookingData.description}
          onChange={handleDescriptionChange}
          className="w-full min-h-[100px]"
        />
      </div>

      {/* Inline review — replaces the removed standalone confirmation step.
          Shows exactly what will be created, in the booker's own zone. */}
      <div className="mt-6 rounded-lg border border-gray-200 bg-card-50 p-4">
        <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-gray-700">
          <Calendar className="h-4 w-4 text-blue-600" aria-hidden />
          Review
        </h4>
        <dl className="space-y-2 text-sm">
          {slot && (
            <div className="flex flex-wrap justify-between gap-2">
              <dt className="text-gray-500">When</dt>
              <dd className="text-right font-medium text-card-foreground">
                {formatSlotDate(slot.start, timeZone)}
                <br />
                {formatTimeRange(slot.start, slot.end, timeZone)} {zoneAbbreviation}
              </dd>
            </div>
          )}
          <div className="flex flex-wrap justify-between gap-2">
            <dt className="text-gray-500">Time zone</dt>
            <dd className="text-right text-card-foreground">
              {formatTimeZoneDisplay(timeZone, slotStart)}
            </dd>
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <dt className="text-gray-500">Where</dt>
            <dd className="text-right text-card-foreground">
              {bookingData.isOnline
                ? 'Google Meet (link in the invite)'
                : bookingData.location || 'In person — add a location above'}
            </dd>
          </div>
          <div className="flex flex-wrap justify-between gap-2">
            <dt className="text-gray-500">Invite goes to</dt>
            <dd className="text-right text-card-foreground">
              {bookingData.email || 'your email address above'}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
